import type { Content, ContentCreateInput } from '@taki/data-model';
import type { ContenuCharge, EtatDeCharge, FormatDeContenu } from '@taki/sdk';
import { estContenuExistant, estFormatIllisible, estFormatTropRecent, lireAuFormat } from '@taki/sdk';
import type { EtatDEnregistrement } from '@taki/ui';

import type { CopieDeSecours, MotifDeCopie, ResultatDePose, TiroirsDuPoste } from './poste.js';
import { empreinteDeTexte } from './poste.js';
import { decisionDeReprise } from './reprise.js';
import type { Blocage, DelaisDeSauvegarde } from './regles.js';
import {
  DELAIS_PAR_DEFAUT,
  blocageDeLaFiche,
  delaiDeRetente,
  doitEcrireMaintenant,
  etatDeLaBande,
  issueDEchec,
  phraseDeBlocage,
  revisionDistanteNouvelle,
  vignetteDue,
} from './regles.js';

/**
 * LE MOTEUR DE SAUVEGARDE D'UN CONTENU OUVERT, sans React.
 *
 * UN MOTEUR PAR OUVERTURE, et c'est ce qui tient le « changement de document »
 * que la PR 212 gerait a coups de numeros de session : quand l'ecran passe a un
 * autre contenu, le moteur de l'ancien est FERME (`fermer`), son ecriture en
 * cours finit sur SON contenu, avec SES etats, et rien de ce qu'elle rend ne
 * touche le suivant. Le hook (`useSauvegardeDeContenu`) ne fait que creer,
 * brancher et fermer des moteurs.
 *
 * CE QU'IL TIENT, et chaque ligne est une lecon payee :
 *
 *  1. DEBOUNCE ET AGE MAXIMUM : on ecrit `debounceMs` apres le dernier geste,
 *     jamais plus de `attenteMaxMs` apres le premier geste pas encore ecrit ;
 *  2. UNE SEULE ECRITURE EN VOL : ce qui arrive pendant le vol est rejoue des
 *     qu'elle rend ;
 *  3. LE BROUILLON AVANT LE RESEAU : chaque ecriture pose d'abord le brouillon
 *     du poste (synchrone), avec la trace de l'ecriture en vol. Une page qui
 *     meurt pendant le vol le retrouve a la reouverture (`reprise.ts`) ;
 *  4. JAMAIS « LE PLUS RECENT GAGNE » EN SILENCE (decision du fondateur,
 *     30/09/2026). Quand quelqu'un a enregistre entre-temps et que du travail
 *     local n'est pas en base, RIEN n'est remplace : le travail local est mis de
 *     cote sur le poste (copie de secours), le conflit s'ANNONCE, et la personne
 *     choisit (`garderMaVersion`, `prendreLaLeur`). Les deux choix gardent une
 *     copie RECUPERABLE de la version qui perd, en base quand les droits le
 *     permettent. Seule une version distante arrivee sur un ecran SANS travail
 *     en attente s'applique d'elle-meme : il n'y a rien a perdre ;
 *  5. ARCHIVE, SUPPRIME, DROITS RETIRES PENDANT L'ECRITURE : le travail qui ne
 *     peut plus s'ecrire en place est mis de cote (copie de secours) AVANT de
 *     passer en lecture seule. La PR 212 faisait l'inverse : la lecture seule
 *     empechait le rejeu ET la copie locale ;
 *  6. LE POSTE QUI REFUSE (stockage plein ou interdit) est un ETAT, pas un
 *     silence : `poste` le dit, et `sauverAvantDePartir` demande de retenir la
 *     page tant qu'un travail n'est nulle part ailleurs ;
 *  7. LA REVISION ET LE FORMAT sont deux choses : la charge se lit par
 *     `lireAuFormat` (`@taki/sdk`), migree et VALIDEE ; un contenu ouvert a un
 *     ancien format s'enregistre au format courant des qu'il s'ecrit.
 */

/** Ce que le moteur demande au SDK, et rien de plus (un faux le remplace en test). */
export interface DepotDeContenus {
  loadContent(id: string): Promise<ContenuCharge | null>;
  saveContent(
    id: string,
    charge: { texte: string; formatVersion: number; preview?: string | null },
    attendu: EtatDeCharge,
  ): Promise<EtatDeCharge>;
  observe(
    id: string,
    onChange: (record: Content | null, meta: { local: boolean }) => void,
    onError?: (error: unknown) => void,
  ): () => void;
  update(id: string, patch: { name?: string; preview?: string | null }): Promise<Content>;
  create(input: ContentCreateInput, options?: { id?: string; texte?: string }): Promise<Content>;
  copy(sourceId: string, options?: { id?: string; name?: string }): Promise<Content>;
  newId(): string;
}

/** Le temps, injecte : les tests le pilotent. */
export interface Horloge {
  maintenant(): number;
  plusTard(fn: () => void, ms: number): unknown;
  annuler(poignee: unknown): void;
}

export const HORLOGE_REELLE: Horloge = {
  maintenant: () => Date.now(),
  plusTard: (fn, ms) => setTimeout(fn, ms),
  annuler: (poignee) => clearTimeout(poignee as ReturnType<typeof setTimeout>),
};

/** Ce qui decrit l'acces de la porte `contents` / `type`, lu par l'appelant. */
export interface AccesAuContenu {
  peutEcrire: boolean;
  /** La phrase du grisement quand `peutEcrire` est faux. */
  raison: string | null;
}

export interface OptionsDeSauvegarde<T> {
  id: string;
  /** Le sous-type que la surface edite : un contenu d'un autre type ne s'ouvre pas. */
  type: string;
  /** L'org courante : les copies naissent dedans. */
  orgId: string;
  /** Le format de la charge (sa version, sa lecture validee, ses migrations). */
  format: FormatDeContenu<T>;
  /** La valeur en texte, AU FORMAT `format.version`. */
  ecrire(valeur: T): string;
  /**
   * La vignette de galerie fabriquee depuis la VALEUR (une petite data URL),
   * ou `null`. Appelee aussi a la fermeture : elle ne doit pas dependre du
   * moteur de l'app, qui est peut-etre deja demonte.
   */
  vignette?: (valeur: T) => Promise<string | null>;
  delais?: Partial<DelaisDeSauvegarde>;
}

export interface DependancesDeSauvegarde {
  contents: DepotDeContenus;
  tiroirs: TiroirsDuPoste;
  acces: AccesAuContenu;
  horloge?: Horloge;
  enLigne?: boolean;
  journal?: Pick<Console, 'error' | 'warn'>;
}

/**
 * CE QUE L'APP MONTE, et quand elle doit le remonter. `generation` change
 * chaque fois que la valeur est REMPLACEE de l'exterieur (ouverture, version
 * distante appliquee, « prendre l'autre version », restauration en place) :
 * une surface React s'en sert comme `key`, un moteur vanilla comme `cle` de
 * `useMoteurVanilla`. Hors de ces cas, la valeur a l'ecran est celle que
 * l'app a signalee, et le moteur ne la touche jamais.
 */
export interface DepartDeContenu<T> {
  valeur: T;
  version: number;
  generation: number;
  origine: 'ouverture' | 'brouillon' | 'distant' | 'autre-version' | 'copie';
}

/** Le conflit annonce : la revision arrivee d'ailleurs, quand, et par qui (un uid). */
export interface ConflitAnnonce {
  versionDistante: number | null;
  quand: Date | null;
  par: string | null;
}

/** La copie de secours, telle que l'ecran l'annonce. */
export interface CopieAnnoncee {
  motif: MotifDeCopie;
  pose: number;
  nom: string;
  /** Octets du texte garde. */
  taille: number;
  /** Elle peut se reprendre EN PLACE (le contenu n'a pas bouge depuis, et il s'ecrit). */
  enPlace: boolean;
}

/** Le dernier geste rendu, pour que l'ecran le dise une fois (« ta version est gardee dans... »). */
export type ArriveeAnnoncee =
  | { type: 'distant'; version: number }
  | { type: 'brouillon-repris' }
  | { type: 'version-gardee'; copieDeLAutre: { id: string; nom: string } | null }
  | { type: 'autre-version-prise'; copieDeLaMienne: { id: string; nom: string } | null }
  | { type: 'copie-restauree'; id: string; enPlace: boolean };

export interface EtatDeSauvegarde<T> {
  chargement: boolean;
  /** L'ouverture a echoue : la phrase. `null` sinon. */
  erreur: string | null;
  depart: DepartDeContenu<T> | null;
  nom: string;
  enregistrement: EtatDEnregistrement;
  lectureSeule: boolean;
  /** POURQUOI rien ne part (lecture seule, refus), en une phrase ; `null` sinon. */
  raison: string | null;
  conflit: ConflitAnnonce | null;
  copie: CopieAnnoncee | null;
  /** Le poste refuse de garder un travail : `absent`, `plein`, `interdit` ; `null` s'il garde. */
  poste: 'absent' | 'plein' | 'interdit' | null;
  horsLigne: boolean;
  /** Un geste de conflit ou de copie est en cours (boutons occupes). */
  occupe: boolean;
  /** Le dernier geste de conflit ou de copie qui a echoue : sa phrase. */
  erreurDeGeste: string | null;
  arrivee: ArriveeAnnoncee | null;
  /** La porte permet de creer un contenu (restaurer une copie en contenu neuf). */
  peutCreer: boolean;
}

/** Les octets UTF-8 d'un texte, sans l'encoder. */
function octets(texte: string): number {
  let total = 0;
  for (let i = 0; i < texte.length; i += 1) {
    const code = texte.charCodeAt(i);
    total += code < 0x80 ? 1 : code < 0x800 ? 2 : code >= 0xd800 && code <= 0xdbff ? 2 : 3;
  }
  return total;
}

/** La date d'une fiche (Timestamp Firestore ou Date), ou `null`. */
function enDate(valeur: unknown): Date | null {
  if (valeur instanceof Date) return valeur;
  if (typeof valeur === 'object' && valeur !== null && typeof (valeur as { toDate?: unknown }).toDate === 'function') {
    return (valeur as { toDate(): Date }).toDate();
  }
  return null;
}

/** Le jour et l'heure, pour nommer une copie (« 1 oct., 14:05 »). */
function horodatage(date: Date): string {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 16).replace('T', ' ');
  }
}

/** Le nom d'une copie, tenu sous le plafond du nom (200). */
function nomDeCopie(nom: string, suffixe: string): string {
  const base = nom.trim() === '' ? 'Sans titre' : nom.trim();
  return `${base.slice(0, 200 - suffixe.length - 1)} ${suffixe}`;
}

/** La phrase d'une ouverture ratee. */
function phraseDOuverture(cause: unknown): string {
  const nom = typeof cause === 'object' && cause !== null ? (cause as { name?: unknown }).name : undefined;
  const code = typeof cause === 'object' && cause !== null ? (cause as { code?: unknown }).code : undefined;
  if (nom === 'ContenuIllisible' || code === 'permission-denied') {
    return "Ce contenu n'existe plus, ou ton accès ne permet pas de l'ouvrir.";
  }
  if (nom === 'ContenuIncoherent') return 'Ce contenu est en train de changer : réessaie dans un instant.';
  if (code === 'unavailable') return 'Connexion perdue : ce contenu ne peut pas être ouvert pour le moment.';
  return "Ce contenu n'a pas pu être ouvert.";
}

/** La phrase d'un geste de conflit ou de copie qui a echoue. */
function phraseDeGeste(cause: unknown): string {
  const issue = issueDEchec(cause);
  if (issue.issue === 'refus' && issue.motif === 'droits') return "Ton accès ne permet pas ce geste : rien n'a été écrit.";
  if (issue.issue === 'trop-lourd') return 'Trop lourd pour être enregistré.';
  if (issue.issue === 'conflit') return 'Le contenu vient encore de changer : regarde la nouvelle version, puis choisis.';
  return "Le geste n'a pas abouti (connexion ?) : rien n'est perdu, réessaie.";
}

export class SauvegardeDeContenu<T> {
  private readonly options: OptionsDeSauvegarde<T>;
  private readonly contents: DepotDeContenus;
  private readonly tiroirs: TiroirsDuPoste;
  private readonly horloge: Horloge;
  private readonly delais: DelaisDeSauvegarde;
  private readonly journal: Pick<Console, 'error' | 'warn'>;
  private readonly abonnes = new Set<() => void>();

  /* ---- Ce que la base porte, a notre connaissance ---- */
  private base: EtatDeCharge | null = null;
  /** Le texte que la base porte (decode, a SON format). */
  private texteBase: string | null = null;
  private fiche: Content | null = null;

  /* ---- La copie de travail ---- */
  private courant: { valeur: T } | null = null;
  /** Du travail n'est pas en base (ni en vol). */
  private modifie = false;
  private premierChangement: number | null = null;
  private minuteur: unknown = null;
  private enVol: { version: number; texte: string } | null = null;
  private aRejouer: { vignette: boolean } | null = null;
  private echecs = 0;
  private echec = false;
  private tropLourd = false;
  private derniereVignette = 0;
  private vignetteEnRetard = false;

  /* ---- Ce qui empeche d'ecrire en place ---- */
  private acces: AccesAuContenu;
  private blocage: Blocage | null = null;
  private conflit: ConflitAnnonce | null = null;
  private copie: CopieDeSecours | null = null;
  private poste: 'absent' | 'plein' | 'interdit' | null;
  private enLigne: boolean;
  private directPerdu = false;

  /* ---- Ce que l'ecran montre ---- */
  private chargement = true;
  private erreur: string | null = null;
  private depart: DepartDeContenu<T> | null = null;
  private generation = 0;
  private nom = '';
  private occupe = false;
  private erreurDeGeste: string | null = null;
  private arrivee: ArriveeAnnoncee | null = null;
  private instantaneCourant: EtatDeSauvegarde<T>;

  /* ---- Les ids tires AVANT un geste, pour le rejouer sans doublon ---- */
  private idCopieDeLAutre: string | null = null;
  private idCopieDeLaMienne: string | null = null;
  private idRestauration: string | null = null;

  private ferme = false;
  private desabonnerDirect: (() => void) | null = null;
  private minuteurDirect: unknown = null;

  /** Le contenu que CE moteur tient (un moteur ne change jamais de contenu). */
  readonly contentId: string;

  constructor(options: OptionsDeSauvegarde<T>, dependances: DependancesDeSauvegarde) {
    this.options = options;
    this.contentId = options.id;
    this.contents = dependances.contents;
    this.tiroirs = dependances.tiroirs;
    this.horloge = dependances.horloge ?? HORLOGE_REELLE;
    this.delais = { ...DELAIS_PAR_DEFAUT, ...options.delais };
    this.journal = dependances.journal ?? console;
    this.acces = dependances.acces;
    this.enLigne = dependances.enLigne ?? true;
    this.poste = dependances.tiroirs.disponible ? null : 'absent';
    this.instantaneCourant = this.calculer();
  }

  /* ======================================================================== */
  /* L'abonnement (useSyncExternalStore)                                      */
  /* ======================================================================== */

  abonner = (abonne: () => void): (() => void) => {
    this.abonnes.add(abonne);
    return () => this.abonnes.delete(abonne);
  };

  instantane = (): EtatDeSauvegarde<T> => this.instantaneCourant;

  private emettre(): void {
    this.instantaneCourant = this.calculer();
    for (const abonne of [...this.abonnes]) abonne();
  }

  private lectureSeule(): boolean {
    return !this.acces.peutEcrire || this.blocage !== null;
  }

  /** On peut ecrire EN PLACE maintenant : droits, contenu vivant, pas de conflit en attente. */
  private peutEcrireEnPlace(): boolean {
    return this.base !== null && !this.lectureSeule() && this.conflit === null;
  }

  private calculer(): EtatDeSauvegarde<T> {
    const refuse = this.blocage !== null && this.blocage !== 'archive';
    const lectureSeule = this.lectureSeule();
    const raison =
      this.blocage !== null ? phraseDeBlocage(this.blocage) : !this.acces.peutEcrire ? this.acces.raison : null;
    const enregistrement = etatDeLaBande({
      refuse,
      lectureSeule,
      conflit: this.conflit !== null,
      tropLourd: this.tropLourd,
      echec: this.echec,
      enVol: this.enVol !== null,
      modifie: this.modifie || this.aRejouer !== null,
    });
    const copie = this.copie;
    return Object.freeze({
      chargement: this.chargement,
      erreur: this.erreur,
      depart: this.depart,
      nom: this.nom,
      enregistrement,
      lectureSeule,
      raison,
      conflit: this.conflit,
      copie:
        copie === null
          ? null
          : {
              motif: copie.motif,
              pose: copie.pose,
              nom: copie.nom,
              taille: octets(copie.texte),
              enPlace:
                copie.motif !== 'conflit' &&
                this.peutEcrireEnPlace() &&
                this.base !== null &&
                copie.versionBase === this.base.version,
            },
      poste: this.poste,
      horsLigne: !this.enLigne || this.directPerdu,
      occupe: this.occupe,
      erreurDeGeste: this.erreurDeGeste,
      arrivee: this.arrivee,
      peutCreer: this.acces.peutEcrire,
    });
  }

  /* ======================================================================== */
  /* Le poste                                                                 */
  /* ======================================================================== */

  private noterPose(resultat: ResultatDePose): boolean {
    this.poste = resultat.ok ? null : resultat.raison;
    return resultat.ok;
  }

  /** Le texte de la copie de travail, ou `null` si elle ne se met pas en texte. */
  private texteCourant(): string | null {
    if (this.courant === null) return null;
    try {
      return this.options.ecrire(this.courant.valeur);
    } catch (cause) {
      this.journal.error('[sauvegarde] La valeur ne se met pas en texte :', cause);
      return null;
    }
  }

  /** Pose le brouillon de la copie de travail (synchrone), avec la trace du vol. */
  private poserBrouillon(texte: string | null = this.texteCourant()): void {
    if (texte === null || this.base === null) return;
    const enVol = this.enVol;
    this.noterPose(
      this.tiroirs.poserBrouillon({
        versionBase: this.base.version,
        formatVersion: this.options.format.version,
        texte,
        ...(enVol !== null ? { enVol: { version: enVol.version, empreinte: empreinteDeTexte(enVol.texte) } } : {}),
      }),
    );
  }

  /**
   * MET LE TRAVAIL LOCAL DE COTE (copie de secours), s'il y en a un qui n'est
   * pas en base. Rend vrai quand le poste l'a garde (ou qu'il n'y avait rien a
   * garder). Si le poste refuse, le travail reste a l'ecran, `modifie` reste
   * vrai, et la fermeture de la page sera retenue.
   */
  private mettreDeCote(motif: MotifDeCopie): boolean {
    const texte = this.texteCourant();
    if (texte === null || this.base === null) return true;
    const enBase = this.texteBase === texte && this.base.formatVersion === this.options.format.version;
    if (enBase && this.enVol === null) {
      this.modifie = false;
      return true;
    }
    // La revision du travail mis de cote est celle sur laquelle il a ete fait.
    const copie = {
      versionBase: this.base.version,
      formatVersion: this.options.format.version,
      texte,
      motif,
      nom: this.nom,
    };
    const garde = this.noterPose(this.tiroirs.poserCopie(copie));
    this.copie = { ...copie, pose: this.horloge.maintenant() };
    if (garde) {
      this.tiroirs.oublierBrouillon();
      this.modifie = false;
    }
    this.premierChangement = null;
    this.arreterMinuteur();
    return garde;
  }

  /* ======================================================================== */
  /* L'ouverture                                                              */
  /* ======================================================================== */

  async ouvrir(): Promise<void> {
    this.chargement = true;
    this.erreur = null;
    this.emettre();
    let lu: ContenuCharge | null;
    try {
      lu = await this.contents.loadContent(this.options.id);
    } catch (cause) {
      if (this.ferme) return;
      this.journal.error('[sauvegarde] Le contenu ne s ouvre pas :', cause);
      /* Seul un refus SUR met le brouillon de cote : une panne de reseau le
         laisse ou il est, pour que « Reessayer » le reprenne. */
      const nom = typeof cause === 'object' && cause !== null ? (cause as { name?: unknown }).name : undefined;
      this.echouerOuverture(phraseDOuverture(cause), nom === 'ContenuIllisible' ? 'introuvable' : null);
      return;
    }
    if (this.ferme) return;
    if (lu === null) {
      this.echouerOuverture('Ce contenu a été supprimé.', 'supprime');
      return;
    }
    if (lu.content.type !== this.options.type) {
      this.echouerOuverture("Ce contenu n'appartient pas à cette app.", null);
      return;
    }

    let lecture: { valeur: T; migre: boolean };
    try {
      lecture = lireAuFormat(this.options.format, lu.texte, lu.content.formatVersion);
    } catch (cause) {
      this.journal.error('[sauvegarde] La charge ne se lit pas a son format :', cause);
      this.echouerOuverture(
        estFormatTropRecent(cause)
          ? "Ce contenu a été enregistré par une version plus récente de l'app : recharge la page."
          : estFormatIllisible(cause)
            ? "Ce contenu ne se lit pas avec cette version de l'app."
            : "Ce contenu n'a pas pu être ouvert.",
        null,
      );
      return;
    }

    this.fiche = lu.content;
    this.base = lu.etat;
    this.texteBase = lu.texte;
    this.nom = lu.content.name;
    this.blocage = blocageDeLaFiche(lu.content) === 'archive' ? 'archive' : null;
    this.copie = this.tiroirs.lireCopie();

    let valeur = lecture.valeur;
    let origine: DepartDeContenu<T>['origine'] = 'ouverture';
    let aEcrire = lecture.migre && this.peutEcrireEnPlace();

    /* LE BROUILLON, quand le poste en a un : reprendre, mettre de cote, ou rien. */
    const brouillon = this.tiroirs.lireBrouillon();
    const empechement = this.blocage === 'archive' ? 'archive' : !this.acces.peutEcrire ? 'droits' : null;
    const decision = decisionDeReprise(
      brouillon,
      { version: lu.etat.version, formatVersion: lu.etat.formatVersion, texte: lu.texte },
      empechement,
    );
    if (decision.issue === 'reprendre') {
      try {
        valeur = lireAuFormat(this.options.format, decision.brouillon.texte, decision.brouillon.formatVersion).valeur;
        origine = 'brouillon';
        aEcrire = true;
        this.arrivee = { type: 'brouillon-repris' };
        this.tiroirs.oublierBrouillon(true);
        // Il reste sur le poste jusqu'a ce que l'ecriture passe : on le repose a notre nom.
      } catch (cause) {
        this.journal.warn('[sauvegarde] Le brouillon ne se lit plus, il est mis de cote :', cause);
        this.garderEnCopie(decision.brouillon.texte, decision.brouillon.formatVersion, decision.brouillon.versionBase, 'format');
      }
    } else if (decision.issue === 'mettre-de-cote') {
      this.garderEnCopie(
        decision.brouillon.texte,
        decision.brouillon.formatVersion,
        decision.brouillon.versionBase,
        decision.motif,
      );
      if (decision.motif === 'conflit') {
        /* LE CONFLIT A L'OUVERTURE : on montre la version de la personne (elle
           l'avait a l'ecran en partant) et on annonce, comme en direct. */
        try {
          valeur = lireAuFormat(this.options.format, decision.brouillon.texte, decision.brouillon.formatVersion).valeur;
          origine = 'brouillon';
          this.conflit = {
            versionDistante: lu.etat.version,
            quand: enDate(lu.content.updatedAt),
            par: typeof lu.content.updatedBy === 'string' ? lu.content.updatedBy : null,
          };
          this.modifie = false;
        } catch {
          // Illisible au format courant : la copie reste telechargeable, l'ecran montre la base.
        }
      }
    } else if (brouillon !== null) {
      this.tiroirs.oublierBrouillon(true);
    }

    this.courant = { valeur };
    this.generation += 1;
    this.depart = { valeur, version: lu.etat.version, generation: this.generation, origine };
    this.chargement = false;
    if (lu.content.preview === null && lu.content.size > 0 && this.options.vignette !== undefined) {
      this.vignetteEnRetard = true;
      this.derniereVignette = 0;
    } else {
      this.derniereVignette = this.horloge.maintenant();
    }
    if (aEcrire) {
      this.modifie = true;
      this.premierChangement = this.horloge.maintenant();
      if (origine === 'brouillon') this.poserBrouillon();
      this.programmer(0);
    }
    this.emettre();
    this.ecouter();
  }

  /** Une copie de secours tiree d'un brouillon ou d'un travail, sans passer par la copie de travail. */
  private garderEnCopie(texte: string, formatVersion: number, versionBase: number, motif: MotifDeCopie): void {
    const copie = { versionBase, formatVersion, texte, motif, nom: this.nom };
    if (this.noterPose(this.tiroirs.poserCopie(copie))) this.tiroirs.oublierBrouillon(true);
    this.copie = { ...copie, pose: this.horloge.maintenant() };
  }

  private echouerOuverture(phrase: string, motif: MotifDeCopie | null): void {
    /* UN BROUILLON SUR UN CONTENU QUI NE S'OUVRE PLUS ne se jette pas : il
       devient une copie de secours, telechargeable ou restaurable ailleurs. */
    const brouillon = this.tiroirs.lireBrouillon();
    if (brouillon !== null && motif !== null) {
      this.garderEnCopie(brouillon.texte, brouillon.formatVersion, brouillon.versionBase, motif);
    } else {
      this.copie = this.tiroirs.lireCopie();
    }
    this.erreur = phrase;
    this.chargement = false;
    this.emettre();
  }

  /** Relance une ouverture ratee (bouton « Reessayer »). */
  reessayerOuverture(): Promise<void> {
    if (this.ferme || this.base !== null) return Promise.resolve();
    return this.ouvrir();
  }

  /* ======================================================================== */
  /* Le direct                                                                */
  /* ======================================================================== */

  private ecouter(): void {
    if (this.ferme || this.desabonnerDirect !== null) return;
    this.desabonnerDirect = this.contents.observe(
      this.options.id,
      (record, { local }) => this.surFiche(record, local),
      (cause) => this.surErreurDuDirect(cause),
    );
  }

  private surErreurDuDirect(cause: unknown): void {
    if (this.ferme) return;
    this.journal.warn('[sauvegarde] La lecture en direct s est arretee :', cause);
    this.desabonnerDirect?.();
    this.desabonnerDirect = null;
    const code = typeof cause === 'object' && cause !== null ? (cause as { code?: unknown }).code : undefined;
    if (code === 'permission-denied') {
      /* LA LECTURE EST REFUSEE : droits retires, ou contenu devenu illisible.
         Le travail en attente est mis de cote AVANT de s'arreter. */
      this.bloquer('droits');
      return;
    }
    this.directPerdu = true;
    this.emettre();
    // Firestore ferme l'ecoute sur erreur : on la rouvre un peu plus tard.
    this.minuteurDirect = this.horloge.plusTard(() => {
      this.minuteurDirect = null;
      this.ecouter();
    }, this.delais.retenteMs);
  }

  private surFiche(record: Content | null, local: boolean): void {
    if (this.ferme || this.base === null) return;
    this.directPerdu = false;
    this.fiche = record ?? this.fiche;
    const blocage = blocageDeLaFiche(record);
    if (blocage === 'supprime' || blocage === 'introuvable') {
      this.bloquer(blocage);
      return;
    }
    if (blocage === 'archive' && this.blocage === null) {
      this.bloquer('archive');
    } else if (blocage === null && this.blocage === 'archive') {
      /* DESARCHIVE AILLEURS : on peut de nouveau ecrire. Une copie mise de cote
         a l'archivage se reprend EN PLACE si le contenu n'a pas bouge depuis. */
      this.blocage = null;
    }
    if (record === null) {
      this.emettre();
      return;
    }
    if (!local && record.name !== this.nom) this.nom = record.name;
    const enVol = this.enVol?.version ?? null;
    if (!revisionDistanteNouvelle(this.base.version, enVol, record.version, local)) {
      this.emettre();
      return;
    }
    if (this.conflit !== null) {
      this.conflit = { versionDistante: record.version, quand: enDate(record.updatedAt), par: record.updatedBy };
      this.emettre();
      return;
    }
    if (this.enVol !== null) {
      // Notre ecriture va se heurter au verrou : c'est elle qui ouvrira le conflit.
      this.emettre();
      return;
    }
    if (this.modifie) {
      this.ouvrirLeConflit({ versionDistante: record.version, quand: enDate(record.updatedAt), par: record.updatedBy });
      return;
    }
    void this.appliquerLaVersionDistante();
  }

  /**
   * UNE VERSION VENUE D'AILLEURS, sur un ecran SANS travail en attente : rien a
   * perdre, on l'applique, et on le dit. Si un geste arrive pendant la
   * relecture, c'est un conflit : on n'ecrase pas ce geste.
   */
  private async appliquerLaVersionDistante(): Promise<void> {
    let lu: ContenuCharge | null;
    try {
      lu = await this.contents.loadContent(this.options.id);
    } catch (cause) {
      this.journal.warn('[sauvegarde] La version distante ne se relit pas :', cause);
      return;
    }
    if (this.ferme || this.base === null) return;
    if (lu === null) {
      this.bloquer('supprime');
      return;
    }
    if (lu.etat.version <= this.base.version) return;
    let valeur: T;
    try {
      valeur = lireAuFormat(this.options.format, lu.texte, lu.etat.formatVersion).valeur;
    } catch (cause) {
      this.journal.error('[sauvegarde] La version distante ne se lit pas :', cause);
      if (estFormatTropRecent(cause)) this.bloquer('format');
      return;
    }
    if (this.modifie || this.enVol !== null || this.conflit !== null) {
      if (this.conflit === null && this.enVol === null) {
        this.ouvrirLeConflit({
          versionDistante: lu.etat.version,
          quand: enDate(lu.content.updatedAt),
          par: lu.content.updatedBy,
        });
      }
      return;
    }
    this.remplacer(lu, valeur, 'distant');
    this.arrivee = { type: 'distant', version: lu.etat.version };
    this.emettre();
  }

  /** Remplace la base ET la valeur a l'ecran (nouvelle generation). */
  private remplacer(lu: ContenuCharge, valeur: T, origine: DepartDeContenu<T>['origine']): void {
    this.base = lu.etat;
    this.texteBase = lu.texte;
    this.fiche = lu.content;
    this.nom = lu.content.name;
    this.courant = { valeur };
    this.modifie = false;
    this.premierChangement = null;
    this.arreterMinuteur();
    this.generation += 1;
    this.depart = { valeur, version: lu.etat.version, generation: this.generation, origine };
  }

  /* ======================================================================== */
  /* Le conflit                                                               */
  /* ======================================================================== */

  /**
   * QUELQU'UN A ENREGISTRE, ET DU TRAVAIL D'ICI N'EST PAS EN BASE. Dans cet
   * ordre, et l'ordre est la regle : la copie de secours D'ABORD (synchrone),
   * l'annonce ensuite. Rien n'est remplace ; plus rien ne s'ecrit en place
   * tant que la personne n'a pas choisi.
   */
  private ouvrirLeConflit(annonce: ConflitAnnonce): void {
    this.arreterMinuteur();
    this.aRejouer = null;
    this.echec = false;
    this.mettreDeCote('conflit');
    this.conflit = annonce;
    this.emettre();
  }

  /**
   * GARDER MA VERSION. La version d'en face n'est pas ecrasee sans trace : elle
   * est d'abord COPIEE en un contenu neuf (« ... (version remplacee le ...) »),
   * puis la mienne s'ecrit par-dessus, a la revision qu'elle porte. L'id de la
   * copie est tire AVANT : un nouvel essai apres une coupure rend la meme copie,
   * sans doublon (`sdk.contents.copy`).
   */
  async garderMaVersion(): Promise<void> {
    if (this.conflit === null || this.occupe || !this.acces.peutEcrire || this.base === null) return;
    const texte = this.texteCourant() ?? this.copie?.texte ?? null;
    if (texte === null) return;
    this.occuper();
    try {
      const lu = await this.contents.loadContent(this.options.id);
      if (this.ferme) return;
      if (lu === null) {
        this.liberer();
        this.bloquer('supprime');
        return;
      }
      this.idCopieDeLAutre ??= this.contents.newId();
      const suffixe = `(version remplacée le ${horodatage(new Date(this.horloge.maintenant()))})`;
      const copie = await this.contents.copy(this.options.id, {
        id: this.idCopieDeLAutre,
        name: nomDeCopie(lu.content.name, suffixe),
      });
      const etat = await this.contents.saveContent(
        this.options.id,
        { texte, formatVersion: this.options.format.version },
        lu.etat,
      );
      if (this.ferme) return;
      this.base = etat;
      this.texteBase = texte;
      this.conflit = null;
      this.modifie = false;
      this.copie = null;
      this.tiroirs.oublierCopie();
      this.tiroirs.oublierBrouillon();
      this.idCopieDeLAutre = null;
      this.vignetteEnRetard = this.options.vignette !== undefined;
      this.arrivee = { type: 'version-gardee', copieDeLAutre: { id: copie.id, nom: copie.name } };
      this.liberer();
      void this.ecrireVignette(true);
    } catch (cause) {
      this.journal.error('[sauvegarde] Garder ma version a echoue :', cause);
      const issue = issueDEchec(cause);
      if (issue.issue === 'conflit') {
        // Une troisieme version est arrivee pendant le geste : sa copie reste a faire.
        this.idCopieDeLAutre = null;
        if (this.conflit !== null) this.conflit = { ...this.conflit, versionDistante: issue.enBase };
      }
      this.liberer(phraseDeGeste(cause));
    }
  }

  /**
   * PRENDRE L'AUTRE VERSION. La mienne n'est pas jetee : elle devient un contenu
   * NEUF (« ... (ma version du ...) ») quand la porte le permet, sinon elle
   * reste en copie de secours sur ce poste, telechargeable. Puis l'ecran passe
   * a l'autre version (nouvelle generation).
   */
  async prendreLaLeur(): Promise<void> {
    if (this.conflit === null || this.occupe) return;
    const texte = this.texteCourant() ?? this.copie?.texte ?? null;
    this.occuper();
    try {
      const lu = await this.contents.loadContent(this.options.id);
      if (this.ferme) return;
      if (lu === null) {
        this.liberer();
        this.bloquer('supprime');
        return;
      }
      const valeur = lireAuFormat(this.options.format, lu.texte, lu.etat.formatVersion).valeur;
      let copieDeLaMienne: { id: string; nom: string } | null = null;
      if (texte !== null && this.acces.peutEcrire) {
        this.idCopieDeLaMienne ??= this.contents.newId();
        const nom = nomDeCopie(this.nom, `(ma version du ${horodatage(new Date(this.horloge.maintenant()))})`);
        try {
          await this.contents.create(
            {
              orgId: this.options.orgId,
              type: this.options.type,
              name: nom,
              mimeType: lu.content.mimeType,
              formatVersion: this.options.format.version,
            },
            { id: this.idCopieDeLaMienne, texte },
          );
          copieDeLaMienne = { id: this.idCopieDeLaMienne, nom };
        } catch (cause) {
          if (estContenuExistant(cause)) {
            copieDeLaMienne = { id: this.idCopieDeLaMienne, nom };
          } else {
            // Elle reste sur ce poste (copie de secours) : rien n'est perdu.
            this.journal.warn('[sauvegarde] Ma version n a pas pu devenir un contenu, elle reste sur le poste :', cause);
          }
        }
      }
      if (this.ferme) return;
      this.remplacer(lu, valeur, 'autre-version');
      this.conflit = null;
      this.idCopieDeLaMienne = null;
      if (copieDeLaMienne !== null) {
        this.copie = null;
        this.tiroirs.oublierCopie();
      }
      this.tiroirs.oublierBrouillon();
      this.arrivee = { type: 'autre-version-prise', copieDeLaMienne };
      this.liberer();
    } catch (cause) {
      this.journal.error('[sauvegarde] Prendre l autre version a echoue :', cause);
      this.liberer(phraseDeGeste(cause));
    }
  }

  /* ======================================================================== */
  /* La copie de secours                                                      */
  /* ======================================================================== */

  /** Ce qu'il faut pour TELECHARGER la copie de secours (l'ecran fabrique le fichier). */
  copieATelecharger(): { nom: string; texte: string; mimeType: string; formatVersion: number } | null {
    const copie = this.copie;
    if (copie === null) return null;
    return {
      nom: copie.nom === '' ? 'Sans titre' : copie.nom,
      texte: copie.texte,
      mimeType: this.fiche?.mimeType ?? 'application/json',
      formatVersion: copie.formatVersion,
    };
  }

  /**
   * RESTAURER LA COPIE DE SECOURS. EN PLACE quand le contenu n'a pas bouge
   * depuis qu'elle a ete mise de cote et qu'il s'ecrit (un desarchivage, des
   * droits rendus) : elle redevient la valeur a l'ecran et s'enregistre. SINON
   * en un contenu NEUF (« ... (copie de secours du ...) ») : rien n'est ecrase.
   * Rend l'id du contenu ou elle vit desormais.
   */
  async restaurerCopie(): Promise<{ id: string; enPlace: boolean } | null> {
    const copie = this.copie;
    if (copie === null || this.occupe || this.conflit !== null) return null;
    const enPlace = this.instantaneCourant.copie?.enPlace === true;
    if (enPlace) {
      let valeur: T;
      try {
        valeur = lireAuFormat(this.options.format, copie.texte, copie.formatVersion).valeur;
      } catch (cause) {
        this.liberer("Cette copie ne se lit pas avec cette version de l'app : télécharge-la.");
        this.journal.error('[sauvegarde] Copie illisible :', cause);
        return null;
      }
      this.courant = { valeur };
      this.generation += 1;
      this.depart = { valeur, version: this.base?.version ?? 0, generation: this.generation, origine: 'copie' };
      this.copie = null;
      this.tiroirs.oublierCopie();
      this.arrivee = { type: 'copie-restauree', id: this.options.id, enPlace: true };
      this.signalerInterne(valeur, true);
      return { id: this.options.id, enPlace: true };
    }
    if (!this.acces.peutEcrire) {
      this.liberer("Ton accès ne permet pas de créer un contenu : télécharge la copie.");
      return null;
    }
    this.occuper();
    this.idRestauration ??= this.contents.newId();
    const nom = nomDeCopie(copie.nom, `(copie de secours du ${horodatage(new Date(copie.pose))})`);
    try {
      try {
        await this.contents.create(
          {
            orgId: this.options.orgId,
            type: this.options.type,
            name: nom,
            mimeType: this.fiche?.mimeType ?? 'application/json',
            formatVersion: copie.formatVersion,
          },
          { id: this.idRestauration, texte: copie.texte },
        );
      } catch (cause) {
        if (!estContenuExistant(cause)) throw cause;
      }
      const id = this.idRestauration;
      this.idRestauration = null;
      this.copie = null;
      this.tiroirs.oublierCopie();
      this.arrivee = { type: 'copie-restauree', id, enPlace: false };
      this.liberer();
      return { id, enPlace: false };
    } catch (cause) {
      this.journal.error('[sauvegarde] La copie n a pas pu etre restauree :', cause);
      this.liberer(phraseDeGeste(cause));
      return null;
    }
  }

  /** JETER LA COPIE DE SECOURS, par un geste explicite (l'ecran demande confirmation). */
  jeterCopie(): void {
    if (this.copie === null || this.conflit !== null) return;
    this.copie = null;
    this.tiroirs.oublierCopie();
    this.emettre();
  }

  /** Oublie l'annonce du dernier geste (l'ecran l'a montree). */
  oublierArrivee(): void {
    if (this.arrivee === null && this.erreurDeGeste === null) return;
    this.arrivee = null;
    this.erreurDeGeste = null;
    this.emettre();
  }

  private occuper(): void {
    this.occupe = true;
    this.erreurDeGeste = null;
    this.emettre();
  }

  private liberer(erreur: string | null = null): void {
    this.occupe = false;
    this.erreurDeGeste = erreur;
    this.emettre();
  }

  /* ======================================================================== */
  /* Les blocages                                                             */
  /* ======================================================================== */

  /**
   * LE CONTENU NE S'ECRIT PLUS EN PLACE (archive, supprime, droits, format).
   * Le travail qui n'est pas en base est mis de cote D'ABORD ; une ecriture en
   * vol finira seule (elle sera acceptee ou refusee, et son refus mettra de
   * cote ce qu'elle portait).
   */
  private bloquer(blocage: Blocage): void {
    if (this.blocage === blocage) {
      this.emettre();
      return;
    }
    this.arreterMinuteur();
    this.aRejouer = null;
    this.echec = false;
    this.tropLourd = false;
    if (this.modifie && this.enVol === null) this.mettreDeCote(blocage);
    // L'archive se leve ; le reste est definitif pour cette ouverture.
    if (this.blocage === null || blocage !== 'archive') this.blocage = blocage;
    this.emettre();
  }

  /** Les droits de la porte ont change (lus par l'appelant). */
  poserAcces(acces: AccesAuContenu): void {
    const avant = this.acces.peutEcrire;
    this.acces = acces;
    if (avant && !acces.peutEcrire) {
      this.arreterMinuteur();
      this.aRejouer = null;
      this.echec = false;
      if (this.modifie && this.enVol === null) this.mettreDeCote('droits');
    }
    this.emettre();
  }

  /** La connexion du navigateur a change. Revenue, une ecriture ratee repart tout de suite. */
  poserEnLigne(enLigne: boolean): void {
    if (this.enLigne === enLigne) return;
    this.enLigne = enLigne;
    if (enLigne && this.echec && this.modifie) this.programmer(0);
    this.emettre();
  }

  /* ======================================================================== */
  /* Les gestes de l'app                                                      */
  /* ======================================================================== */

  /** A CHAQUE changement du moteur de l'app : garde la copie de travail, arme l'ecriture. */
  signaler(valeur: T): void {
    this.signalerInterne(valeur, false);
  }

  private signalerInterne(valeur: T, maintenant: boolean): void {
    if (this.ferme || this.base === null || this.chargement) return;
    this.courant = { valeur };
    this.modifie = true;
    this.tropLourd = false;
    const now = this.horloge.maintenant();
    this.premierChangement ??= now;
    if (this.conflit !== null || this.lectureSeule()) {
      /* RIEN NE S'ECRIT EN PLACE, et rien ne se perd : la copie de secours suit
         le travail, au rythme du debounce. */
      this.programmer(this.delais.debounceMs);
      this.emettre();
      return;
    }
    if (maintenant || doitEcrireMaintenant(this.premierChangement, now, this.delais.attenteMaxMs)) {
      this.programmer(0);
    } else {
      this.programmer(this.delais.debounceMs);
    }
    this.emettre();
  }

  /** Ecrit tout de suite ce qui attend (un bouton, un raccourci). */
  enregistrerMaintenant(): Promise<void> {
    this.arreterMinuteur();
    return this.enregistrer(true);
  }

  async renommer(nom: string): Promise<void> {
    const propre = nom.trim();
    if (propre === '' || propre === this.nom || this.lectureSeule() || this.base === null) return;
    const avant = this.nom;
    this.nom = propre;
    this.emettre();
    try {
      await this.contents.update(this.options.id, { name: propre });
    } catch (cause) {
      this.nom = avant;
      this.emettre();
      throw cause;
    }
  }

  /* ======================================================================== */
  /* L'ecriture                                                               */
  /* ======================================================================== */

  private arreterMinuteur(): void {
    if (this.minuteur !== null) {
      this.horloge.annuler(this.minuteur);
      this.minuteur = null;
    }
  }

  private programmer(ms: number, forcerVignette = false): void {
    this.arreterMinuteur();
    // Ferme, plus rien ne se programme : le brouillon du poste garde le travail.
    if (this.ferme) return;
    this.minuteur = this.horloge.plusTard(() => {
      this.minuteur = null;
      void this.enregistrer(forcerVignette);
    }, ms);
  }

  private async enregistrer(forcerVignette: boolean): Promise<void> {
    if (this.base === null || this.courant === null) return;
    if (this.conflit !== null) {
      // Le travail suit dans la copie de secours, jamais en place.
      if (this.modifie) this.mettreDeCote('conflit');
      this.emettre();
      return;
    }
    if (this.lectureSeule()) {
      if (this.modifie && this.enVol === null) {
        this.mettreDeCote(this.blocage ?? 'droits');
        this.emettre();
      }
      return;
    }
    if (this.enVol !== null) {
      // Une seule ecriture en vol : celle-ci repartira derriere, et le poste la garde deja.
      this.aRejouer = { vignette: forcerVignette || this.aRejouer?.vignette === true };
      this.poserBrouillon();
      this.emettre();
      return;
    }
    const texte = this.texteCourant();
    if (texte === null) {
      this.echec = true;
      this.emettre();
      return;
    }
    const attendu = this.base;
    if (texte === this.texteBase && attendu.formatVersion === this.options.format.version) {
      // Rien de neuf : la base porte deja ce qu'il y a a l'ecran.
      this.modifie = false;
      this.premierChangement = null;
      this.echec = false;
      this.tiroirs.oublierBrouillon();
      this.emettre();
      if (forcerVignette || this.vignetteEnRetard) void this.ecrireVignette(forcerVignette);
      return;
    }

    this.enVol = { version: attendu.version + 1, texte };
    this.modifie = false;
    this.premierChangement = null;
    this.poserBrouillon(texte);
    this.emettre();

    try {
      const etat = await this.contents.saveContent(
        this.options.id,
        { texte, formatVersion: this.options.format.version },
        attendu,
      );
      this.enVol = null;
      this.base = etat;
      this.texteBase = texte;
      this.echecs = 0;
      this.echec = false;
      this.tropLourd = false;
      this.vignetteEnRetard = this.options.vignette !== undefined;
      if (!this.modifie && this.aRejouer === null) this.tiroirs.oublierBrouillon();
    } catch (cause) {
      this.enVol = null;
      this.modifie = true;
      const issue = issueDEchec(cause);
      if (issue.issue === 'conflit') {
        this.journal.warn('[sauvegarde] Quelqu un a enregistre entre-temps :', cause);
        let annonce: ConflitAnnonce = { versionDistante: issue.enBase, quand: null, par: null };
        try {
          const fiche = this.fiche;
          if (fiche !== null && fiche.version === issue.enBase) {
            annonce = { versionDistante: issue.enBase, quand: enDate(fiche.updatedAt), par: fiche.updatedBy };
          }
        } catch {
          // L'annonce sans auteur ni date dit deja l'essentiel.
        }
        this.ouvrirLeConflit(annonce);
        return;
      }
      if (issue.issue === 'refus') {
        this.journal.error('[sauvegarde] L enregistrement est refuse pour de bon :', cause);
        /* `bloquer` ne refait rien pour un blocage deja vu (l'archive, apprise
           par le direct pendant le vol) : on l'efface d'abord, pour que le
           travail que portait l'ecriture refusee soit bien mis de cote. */
        this.blocage = null;
        this.bloquer(issue.motif === 'conflit' ? 'droits' : issue.motif);
        return;
      }
      if (issue.issue === 'trop-lourd') {
        this.tropLourd = true;
        this.poserBrouillon();
        this.emettre();
        return;
      }
      this.journal.warn('[sauvegarde] L enregistrement a echoue, nouvel essai programme :', cause);
      this.echec = true;
      this.echecs += 1;
      this.poserBrouillon();
      this.programmer(delaiDeRetente(this.echecs, this.delais.retenteMs, this.delais.retentePlafondMs), forcerVignette);
      this.emettre();
      return;
    }

    const aRejouer = this.aRejouer;
    this.aRejouer = null;
    this.emettre();
    if (aRejouer !== null || this.modifie) {
      void this.enregistrer(aRejouer?.vignette === true || forcerVignette);
      return;
    }
    void this.ecrireVignette(forcerVignette);
  }

  /** La vignette suit l'ecriture, ne la bloque jamais, et ne se dit qu'en console si elle rate. */
  private async ecrireVignette(forcer: boolean): Promise<void> {
    const fabriquer = this.options.vignette;
    const courant = this.courant;
    if (fabriquer === undefined || courant === null || !this.vignetteEnRetard || this.lectureSeule()) return;
    const maintenant = this.horloge.maintenant();
    if (!vignetteDue(this.derniereVignette, maintenant, this.delais.vignetteMs, forcer)) return;
    this.derniereVignette = maintenant;
    this.vignetteEnRetard = false;
    try {
      const apercu = await fabriquer(courant.valeur);
      if (apercu !== null) await this.contents.update(this.options.id, { preview: apercu });
    } catch (cause) {
      this.journal.warn('[sauvegarde] La vignette n a pas pu etre ecrite :', cause);
    }
  }

  /* ======================================================================== */
  /* La sortie                                                                */
  /* ======================================================================== */

  /**
   * LE DERNIER GESTE AVANT QUE LA PAGE PARTE (`beforeunload`, `pagehide`, onglet
   * cache), et le seul qui tienne : une copie LOCALE, synchrone. Puis
   * l'ecriture quand meme (si la page ne part pas, elle arrivera). Rend
   * `retenir: true` quand un travail n'est NULLE PART ailleurs qu'a l'ecran (le
   * poste a refuse de le garder) : l'appelant demande alors au navigateur de
   * confirmer la fermeture.
   */
  sauverAvantDePartir(): { retenir: boolean } {
    if (this.base === null) return { retenir: false };
    const travail = this.modifie || this.enVol !== null || this.aRejouer !== null;
    if (!travail) return { retenir: false };
    if (this.conflit !== null || this.lectureSeule()) {
      const garde = this.mettreDeCote(this.conflit !== null ? 'conflit' : this.blocage ?? 'droits');
      this.emettre();
      return { retenir: !garde };
    }
    this.poserBrouillon();
    const garde = this.poste === null;
    if (this.modifie && this.enVol === null) {
      this.arreterMinuteur();
      void this.enregistrer(true);
    }
    this.emettre();
    return { retenir: !garde };
  }

  /**
   * FERMER : l'ecran quitte ce contenu (autre contenu, demontage). Ce qui
   * attend part maintenant, vignette comprise, depuis la COPIE DE TRAVAIL (le
   * moteur de l'app est peut-etre deja demonte) ; le brouillon le garde au cas
   * ou. Les ecritures en cours finissent sur CE contenu, et plus rien ne
   * s'emet.
   */
  fermer(): void {
    if (this.ferme) return;
    this.desabonnerDirect?.();
    this.desabonnerDirect = null;
    if (this.minuteurDirect !== null) this.horloge.annuler(this.minuteurDirect);
    if (this.base !== null) {
      const travail = this.modifie || this.aRejouer !== null;
      if (travail && (this.conflit !== null || this.lectureSeule())) {
        this.mettreDeCote(this.conflit !== null ? 'conflit' : this.blocage ?? 'droits');
      } else if (travail || this.enVol !== null) {
        this.poserBrouillon();
        if (this.modifie && this.enVol === null) {
          this.arreterMinuteur();
          void this.enregistrer(true);
        }
      } else if (this.vignetteEnRetard) {
        void this.ecrireVignette(true);
      }
    }
    this.ferme = true;
    this.abonnes.clear();
  }

  /** Vrai une fois `fermer` appele (les tests le lisent). */
  get estFerme(): boolean {
    return this.ferme;
  }
}
